import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { VALIDATION_RULES, validateForm } from "../../../utils/formValidation";
import { CityField } from "../../common/CityField";
import { FieldLayout } from "../../common/FieldFrame";
import { FormInput } from "../../common/FormLayout";

const BREEDING_RULES = {
  name: [VALIDATION_RULES.required(), VALIDATION_RULES.minLength(2)],
  address: [VALIDATION_RULES.required(), VALIDATION_RULES.minLength(5)],
  zip_code: [VALIDATION_RULES.required(), VALIDATION_RULES.postalCode()],
  city: [VALIDATION_RULES.required()],
};

export type BreedingFormValues = {
  name: string;
  address: string;
  address_complementary: string;
  zip_code: string;
  city: string;
};

export const EMPTY_BREEDING_FORM: BreedingFormValues = {
  name: "",
  address: "",
  address_complementary: "",
  zip_code: "",
  city: "",
};

/** État et validation du formulaire d'élevage, partagés par la mise en route et la fiche élevage. */
export const useBreedingForm = (initial: BreedingFormValues) => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const change = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Changer de code postal invalide la ville choisie pour l'ancien.
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "zip_code" && { city: "" }),
    }));
    setErrors((prev) => {
      const { [name]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const changeCity = (city: string, zipCode: string) => {
    setForm((prev) => ({ ...prev, city, zip_code: zipCode }));
    setErrors((prev) => {
      const { city: _city, zip_code: _zip, ...rest } = prev;
      return rest;
    });
  };

  /** Vrai si le formulaire est valide ; sinon affiche les erreurs. */
  const validate = (): boolean => {
    const found = validateForm(form, BREEDING_RULES);
    setErrors(found);
    return Object.keys(found).length === 0;
  };

  // Stable : un parent peut le rappeler dans un effet (réouverture d'une modale).
  const reset = useCallback((values: BreedingFormValues) => {
    setForm(values);
    setErrors({});
  }, []);

  return { form, errors, change, changeCity, validate, reset };
};

export const BreedingFields = ({
  breedingForm,
  layout = "stacked",
}: {
  breedingForm: ReturnType<typeof useBreedingForm>;
  layout?: FieldLayout;
}) => {
  const { t } = useTranslation();
  const { form, errors, change, changeCity } = breedingForm;
  const zipCodeField = (
    <FormInput
      label={t("company-zip-code")}
      layout={layout}
      name="zip_code"
      value={form.zip_code}
      onChange={change}
      required
      error={errors.zip_code}
    />
  );
  const cityField = (
    <CityField
      layout={layout}
      zipCode={form.zip_code}
      value={form.city}
      onChange={changeCity}
      required
      error={errors.city}
    />
  );
  return (
    <>
      <FormInput
        layout={layout}
        label={t("breeding-name")}
        name="name"
        value={form.name}
        onChange={change}
        required
        error={errors.name}
      />
      <FormInput
        layout={layout}
        label={t("company-address")}
        name="address"
        value={form.address}
        onChange={change}
        required
        error={errors.address}
      />
      <FormInput
        layout={layout}
        label={t("company-address-complementary")}
        name="address_complementary"
        value={form.address_complementary}
        onChange={change}
      />
      {layout === "row" ? (
        <>
          {zipCodeField}
          {cityField}
        </>
      ) : (
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">{zipCodeField}</div>
          <div className="flex-1">{cityField}</div>
        </div>
      )}
    </>
  );
};
